// MCP Apps（SEP-1865，協定版本 2026-01-26）宿主端純函式（AGUI-1274，簡報建立畫面）。
//
// 工具結果可能帶一個 `mcpApp` 標記：代表該工具在 `ui://` 資源上提供互動畫面。前端經
// POST /web/chat/{chatId}/mcp-apps/resource（mygenieap → relay）讀回 HTML，放進沙箱 iframe，
// 再以 JSON-RPC postMessage 與畫面對話（見 components/McpAppFrame.js）。
// 本檔全部是純函式（移植自 GenAIus 參考實作 mcp-apps.ts），不碰瀏覽器 API 以外的狀態，方便單元測試。

export const MCP_APP_PROTOCOL_VERSION = '2026-01-26';
export const MCP_APP_MIME_TYPE = 'text/html;profile=mcp-app';
const MCP_APP_RESOURCE_SCHEME = 'ui://';

function asRecord(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

function asText(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * 正規化 mcpApp 標記：同時接受 relay 的 camelCase（mcpApp）與 agentbot 原始的 snake_case（mcp_app）。
 * 只有 resourceUri 以 `ui://` 開頭且 server 不為空才視為有效，否則回傳 undefined。
 *
 * @param {unknown} value 標記物件
 * @returns {{resourceUri: string, server: string, toolName: string, toolInput?: object, toolResult?: object}|undefined}
 */
export function normalizeMcpAppMarker(value) {
  const record = asRecord(value);
  if (!record) return undefined;
  const resourceUri = asText(record.resourceUri ?? record.resource_uri);
  const server = asText(record.server);
  if (!resourceUri.startsWith(MCP_APP_RESOURCE_SCHEME) || !server) return undefined;
  const marker = {
    resourceUri,
    server,
    toolName: asText(record.toolName ?? record.tool_name),
  };
  const toolInput = asRecord(record.toolInput ?? record.tool_input);
  if (toolInput) marker.toolInput = toolInput;
  const toolResult = asRecord(record.toolResult ?? record.tool_result);
  if (toolResult) marker.toolResult = toolResult;
  return marker;
}

/**
 * 正規化「掛在訊息上」的一筆互動畫面：標記內容 + toolCallId（去重用）。
 * toolCallId 取自標記本身（/reply 的 data.mcpApps[] 帶 toolCallId），沒有時用 fallbackToolCallId
 * （阻塞 /send 的 tool_calls[] 由外層的 id 提供）。
 *
 * @param {unknown} value 標記物件
 * @param {string} [fallbackToolCallId]
 * @returns {object|undefined}
 */
export function normalizeMcpAppEntry(value, fallbackToolCallId) {
  const marker = normalizeMcpAppMarker(value);
  if (!marker) return undefined;
  const record = asRecord(value);
  const toolCallId = asText(record.toolCallId ?? record.tool_call_id) || asText(fallbackToolCallId);
  return toolCallId ? { toolCallId, ...marker } : marker;
}

// 去重鍵：同一個 tool_call_id 只顯示一次；沒有 id 時退回以 server + uri 判斷
const mcpAppKey = (entry) => entry.toolCallId || `${entry.server}|${entry.resourceUri}`;

/**
 * 合併多份互動畫面清單並依 toolCallId 去重（保留先出現者），無效標記一律剔除。
 *
 * @param {...unknown} lists 任意多個陣列（非陣列會被忽略）
 * @returns {object[]}
 */
export function mergeMcpApps(...lists) {
  const seen = new Set();
  const merged = [];
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      const entry = normalizeMcpAppEntry(item);
      if (!entry) continue;
      const key = mcpAppKey(entry);
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(entry);
    }
  }
  return merged;
}

/**
 * 從阻塞 /send 回應的 metadata.tool_list 取出互動畫面：
 * tool_list[].tool_calls[] 中帶 mcpApp（或 mcp_app）的項目，以該 tool call 的 id 當 toolCallId。
 * 工具狀態為錯誤者不顯示。
 *
 * @param {unknown} toolList
 * @returns {object[]}
 */
export function extractMcpAppsFromToolList(toolList) {
  if (!Array.isArray(toolList)) return [];
  const entries = [];
  for (const tool of toolList) {
    const calls = Array.isArray(tool?.tool_calls) ? tool.tool_calls : [];
    for (const call of calls) {
      if (!asRecord(call) || call.status === 'error') continue;
      const entry = normalizeMcpAppEntry(call.mcpApp ?? call.mcp_app, call.id ?? call.toolCallId ?? call.tool_call_id);
      if (entry) entries.push(entry);
    }
  }
  return mergeMcpApps(entries);
}

/**
 * 對話中是否有「可操作」的互動畫面：最後一則使用者訊息之後，有帶 mcpApps 的非串流回覆。
 * 判斷與畫面一致（App.js 的 mcpAppsInteractive、ChatMessage 的 mcpApps 顯示條件），
 * 只有這種對話才會真的載入 iframe；已完成的畫面只顯示一行文字。
 * 供切換對話時決定是否要「先排好再顯示」（AGUI-1302）。
 *
 * @param {unknown} messages 對話訊息陣列
 * @returns {boolean}
 */
export function hasInteractiveMcpApp(messages) {
  if (!Array.isArray(messages)) return false;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message?.sender === 'guest') return false;
    if (!message?.isStreaming && Array.isArray(message?.mcpApps) && message.mcpApps.length > 0) return true;
  }
  return false;
}

/**
 * 互動畫面的顯示標題：簡報模板選擇器固定叫「簡報建立畫面」，其餘一律「互動畫面」。
 *
 * @param {string} toolName
 * @returns {string}
 */
export function mcpAppTitle(toolName) {
  return typeof toolName === 'string' && toolName.includes('open_ppt_template_picker')
    ? '簡報建立畫面'
    : '互動畫面';
}

function declaredOrigins(values) {
  const origins = [];
  for (const value of (Array.isArray(values) ? values : []).slice(0, 16)) {
    try {
      const url = new URL(String(value));
      if ((url.protocol === 'https:' || url.protocol === 'http:') && !origins.includes(url.origin)) {
        origins.push(url.origin);
      }
    } catch (e) {
      // 格式不正確的網域直接忽略，不可因此放寬政策
    }
  }
  return origins;
}

/**
 * 在 HTML 的 <head> 之後插入 CSP meta，只放行資源在 _meta.ui.csp 宣告的網域；
 * 沒宣告 connectDomains 時 connect-src 為 'none'（完全不能連網）。
 * iframe 本身不給 allow-same-origin，畫面也碰不到 MyBro 的 cookie／storage。
 *
 * @param {string} html
 * @param {object|null|undefined} meta 資源回應的 meta
 * @returns {string}
 */
export function withResourceCsp(html, meta) {
  const ui = asRecord(asRecord(meta)?.ui) || {};
  const declared = asRecord(ui.csp) || {};
  const resources = declaredOrigins(declared.resourceDomains).join(' ');
  const connections = declaredOrigins(declared.connectDomains).join(' ');
  const extra = resources ? ` ${resources}` : '';
  const csp =
    [
      "default-src 'none'",
      `script-src 'unsafe-inline' 'unsafe-eval' blob:${extra}`,
      `style-src 'unsafe-inline'${extra}`,
      `img-src data: blob:${extra}`,
      `font-src data:${extra}`,
      `media-src data: blob:${extra}`,
      `connect-src ${connections || "'none'"}`,
    ].join('; ') + ';';
  const metaTag = `<meta http-equiv="Content-Security-Policy" content="${csp}">`;
  const source = typeof html === 'string' ? html : '';
  if (/<head(?:\s[^>]*)?>/i.test(source)) return source.replace(/<head(?:\s[^>]*)?>/i, (match) => match + metaTag);
  return `<head>${metaTag}</head>${source}`;
}

/**
 * 取出 ui/message 要以使用者身分送出的文字。
 * content 可能是單一 block 或 block 陣列；取所有 type:"text" 的 text 以換行串接。
 * role 不是 user（或沒有文字）時回傳空字串，由呼叫端回 -32602。
 *
 * @param {unknown} params
 * @returns {string}
 */
export function uiMessageText(params) {
  const record = asRecord(params);
  if (!record || asText(record.role || 'user') !== 'user') return '';
  const content = record.content;
  const blocks = Array.isArray(content) ? content : [content];
  return blocks
    .map((block) => {
      const item = asRecord(block);
      return item && item.type === 'text' ? asText(item.text) : '';
    })
    .filter(Boolean)
    .join('\n')
    .trim();
}

/**
 * 限制畫面高度在 [minimum, maximum]；非數字回傳 null（忽略這次回報）。
 */
export function clampAppHeight(requested, minimum, maximum) {
  if (requested === null || requested === undefined || requested === '') return null;
  const value = Number(requested);
  if (!Number.isFinite(value)) return null;
  return Math.max(minimum, Math.min(maximum, Math.ceil(value)));
}

/** ui/open-link 只允許 http(s) 連結，其餘回傳 null。 */
export function safeExternalUrl(value) {
  try {
    const url = new URL(String(value || ''));
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null;
  } catch (e) {
    return null;
  }
}
